/**
 * Camera + QR decoding for the launcher's "Scan a QR code" (`/join/scan`). The native
 * `BarcodeDetector` is used where it reads `qr_code` (Chrome on Android/macOS/ChromeOS);
 * elsewhere (iOS Safari, Firefox) `jsqr` decodes canvas frames. jsqr is imported on first
 * use only, so it is a separate chunk that members who never scan never download.
 */
import { useEffect, useState } from 'react';

export type QrDetector = {
  kind: 'native' | 'jsqr';
  /** The raw values of the QR codes visible in the current frame (usually zero or one). */
  detect: (video: HTMLVideoElement) => Promise<string[]>;
};

type NativeBarcodeDetector = { detect: (source: CanvasImageSource) => Promise<{ rawValue: string }[]> };
type NativeBarcodeDetectorClass = {
  new (options: { formats: string[] }): NativeBarcodeDetector;
  getSupportedFormats: () => Promise<string[]>;
};

/** `getUserMedia` exists (secure context, a browser that has it). */
export function cameraSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';
}

/**
 * Whether to offer "Scan a QR code": `getUserMedia` exists and, where the browser can tell
 * before permission, a video input does too (browsers list one per kind without labels).
 */
export function useCameraAvailable(): boolean {
  const [available, setAvailable] = useState(cameraSupported);
  useEffect(() => {
    if (!available || typeof navigator.mediaDevices.enumerateDevices !== 'function') return;
    let cancelled = false;
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (!cancelled && !devices.some((d) => d.kind === 'videoinput')) setAvailable(false);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  return available;
}

/** The back camera when there is one (`ideal`, so a laptop's only camera is fine too). */
export function openCamera(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
}

export function stopStream(stream: MediaStream | null): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
}

export type CameraProblem = 'denied' | 'no-camera' | 'busy';

/** What a `getUserMedia` rejection means for the person holding the phone. */
export function cameraProblem(err: unknown): CameraProblem {
  const name = err instanceof Error || (typeof err === 'object' && err !== null && 'name' in err) ? String((err as { name: unknown }).name) : '';
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') return 'denied';
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') return 'no-camera';
  return 'busy';
}

async function nativeDetector(): Promise<QrDetector | null> {
  const Detector = (globalThis as { BarcodeDetector?: NativeBarcodeDetectorClass }).BarcodeDetector;
  if (!Detector) return null;
  try {
    if (!(await Detector.getSupportedFormats()).includes('qr_code')) return null;
    const detector = new Detector({ formats: ['qr_code'] });
    return {
      kind: 'native',
      detect: async (video) => (await detector.detect(video)).map((c) => c.rawValue),
    };
  } catch {
    return null;
  }
}

/** Frames are scaled down to this many pixels on the long side before jsqr reads them. */
const JSQR_MAX_SIDE = 720;

async function jsqrDetector(): Promise<QrDetector> {
  const { default: jsQR } = await import('jsqr');
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  return {
    kind: 'jsqr',
    detect: async (video) => {
      const { videoWidth: w, videoHeight: h } = video;
      if (!ctx || w === 0 || h === 0) return [];
      const scale = Math.min(1, JSQR_MAX_SIDE / Math.max(w, h));
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(frame.data, frame.width, frame.height, { inversionAttempts: 'attemptBoth' });
      return code?.data ? [code.data] : [];
    },
  };
}

/** The native detector when it reads QR codes, else jsqr (loaded now). */
export async function createQrDetector(): Promise<QrDetector> {
  return (await nativeDetector()) ?? jsqrDetector();
}
